import {
    Button,
    Caption,
    Flex,
    SearchableDropdown,
    useAppDisplayModeContext,
    useStyles,
} from '@archipro-design/aria';
import { Animation } from '@fluentui/react-northstar';
import { CustomLocation, Loader, SearchLine } from '@archipro-design/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { DropdownItem } from '~/modules/enquiry/component/enquiry-form/EnquiryForm';
import type { BasicPlace } from '~/modules/location/type';
import { formatSuburbDropdownOptionsWithPrefix } from '~/modules/enquiry/util/format-place-dropdown-options';
import type { IUseCurrentLocationResult } from '~/modules/location/hook/use-current-location';
import type { SearchLocation } from '../../type/api';
import type { FilterStatesKey } from '../../type/filter-state';
import * as S from './FilterLocation.style';
import { useNavigation, useSearchParams } from '@remix-run/react';
import { useCountryContext } from '@archipro-website/localisation';
import type { DetailedPlace } from '~/modules/location/type';

export interface LocationFilter {
    kind: 'Location';
    filterKey: FilterStatesKey;
    subFilterKey?: string;
}

export interface LocationFilterState extends Pick<LocationFilter, 'kind'> {
    selectedLocation?: SearchLocation;
}

interface FilterLocationProps extends IUseCurrentLocationResult {
    filter: LocationFilter;
    state: LocationFilterState;
    updateFilter: (
        filterKey: FilterStatesKey,
        state: Partial<LocationFilterState>,
        updateStates?: boolean
    ) => void;
    findPlace: (placeSearch?: string | undefined) => Promise<void> | undefined;
    places: DetailedPlace[];
}

const mapToSearchLocations = (places: DetailedPlace[]): SearchLocation[] => {
    return places.map((place) => {
        return {
            suburb: place.suburb,
            district: place.district,
            city: place.city,
            region: place.region,
            state: place.state,
            country: place.country,
        };
    });
};

const FilterLocation = ({
    filter,
    state,
    updateFilter,
    findPlace,
    places,
    currentLocationDisabled,
    currentLocation,
    getCurrentLocation,
    loading,
}: FilterLocationProps) => {
    const { desktop } = useAppDisplayModeContext();

    const navigation = useNavigation();
    const [searchParams] = useSearchParams();

    const { filterKey } = filter;
    const requiredUseCurrentLocation = useRef(false);
    const [searchQuery, setSearchQuery] = useState<string | undefined>(
        undefined
    );
    const { selectedLocation } = state;
    const availablePlaces = mapToSearchLocations(places);
    let selectedIndex = -1;
    const { code: siteCountryCode } = useCountryContext();
    if (selectedLocation) {
        selectedIndex = availablePlaces.findIndex(
            (p) =>
                p.suburb === selectedLocation.suburb &&
                p.district === selectedLocation.district &&
                p.city === selectedLocation.city &&
                p.region === selectedLocation.region &&
                p.state === selectedLocation.state &&
                p.country === selectedLocation.country
        );
        if (selectedIndex === -1) {
            availablePlaces.push(selectedLocation);
            selectedIndex = availablePlaces.length - 1;
        }
    }
    const options = formatSuburbDropdownOptionsWithPrefix(
        availablePlaces,
        selectedIndex
    );

    const selectedOption = options.find((option) => option.selected);
    // arrange the options in the order of Region, City, Suburb
    const sortedOptions = options.sort((a, b) => {
        const prefixOrder = ['Region,', 'City,', 'Suburb,'];
        const aIndex = prefixOrder.indexOf(a?.prefix);
        const bIndex = prefixOrder.indexOf(b?.prefix);

        return aIndex - bIndex;
    });

    useEffect(() => {
        if (loading) return;
        if (currentLocation && requiredUseCurrentLocation.current) {
            updateFilter(filterKey, {
                kind: 'Location',
                selectedLocation: currentLocation,
            });
        }
        requiredUseCurrentLocation.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentLocation, filterKey, loading]);

    const handleUseCurrentLocation = useCallback(() => {
        if (currentLocation) {
            // don't update the filter state if it's the same location
            // it will cause updateFilter change
            if (
                currentLocation.suburb === selectedLocation?.suburb &&
                currentLocation.district === selectedLocation?.district &&
                currentLocation.city === selectedLocation?.city &&
                currentLocation.region === selectedLocation?.region &&
                currentLocation.state === selectedLocation?.state &&
                currentLocation.country === selectedLocation?.country
            ) {
                return;
            }
            updateFilter(filterKey, {
                kind: 'Location',
                selectedLocation: currentLocation,
            });
        } else {
            if (!requiredUseCurrentLocation.current) {
                requiredUseCurrentLocation.current = true;
                getCurrentLocation();
            }
        }
    }, [
        currentLocation,
        filterKey,
        getCurrentLocation,
        updateFilter,
        selectedLocation,
    ]);

    const currentSearchQuery = selectedOption?.header ?? searchQuery;

    const useCurrentLocation = searchParams.get('currentLocation') === '1';

    const { css } = useStyles({
        hasValue: !!selectedOption,
    });

    useEffect(() => {
        if (!useCurrentLocation) return;
        handleUseCurrentLocation();
    }, [useCurrentLocation, handleUseCurrentLocation]);

    return (
        <Flex column className={css(S.FilterLocation)}>
            <SearchableDropdown
                disabled={navigation.state === 'loading'}
                items={sortedOptions.map((option, i) => ({
                    ...option,
                    content: (
                        <Caption className={css(S.Prefix)} variant="03">
                            {siteCountryCode === 'AU' &&
                            option.prefix === 'Region,'
                                ? 'State,'
                                : option.prefix}
                        </Caption>
                    ),
                    className: css(S.SuggestionDropdown),
                    key: option.header + i,
                }))}
                value={selectedOption}
                searchQuery={currentSearchQuery}
                clearable={!!currentSearchQuery}
                onSearchQueryChange={(_, data) => {
                    if (
                        data.searchQuery &&
                        data.searchQuery === selectedOption?.header
                    )
                        return;
                    setSearchQuery(data.searchQuery);
                    findPlace(data.searchQuery);
                }}
                searchInput={{
                    placeholder: 'Postcode, suburb or region',
                    disabled: loading && requiredUseCurrentLocation.current,
                }}
                onChange={(_, data) => {
                    let selectedPlace: DropdownItem<BasicPlace> | null = null;
                    if (data.value) {
                        selectedPlace = data.value as DropdownItem<BasicPlace>;
                    } else {
                        requiredUseCurrentLocation.current = false;
                    }
                    setSearchQuery('');
                    updateFilter(filterKey, {
                        kind: 'Location',
                        selectedLocation: selectedPlace?.value,
                    });
                }}
                toggleIndicator={
                    loading && requiredUseCurrentLocation.current ? (
                        <Animation name="rotateUltraSlow">
                            <Loader size="2x" />
                        </Animation>
                    ) : (
                        <SearchLine size="2x" />
                    )
                }
            />
            <Button
                className="currentLocationButton"
                variant="text"
                size={desktop ? 20 : 16}
                iconBefore={<CustomLocation />}
                disabled={navigation.state === 'loading'}
                variables={{ buttonPadding: 0, iconPadding: 0 }}
                onClick={handleUseCurrentLocation}
            >
                Use my current location
            </Button>
            {currentLocationDisabled && (
                <Caption className="currentLocationNote">
                    Please change your browser settings to allow location
                    services.
                </Caption>
            )}
        </Flex>
    );
};

export default FilterLocation;
